import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CreditCardIcon,
  CheckCircleIcon,
  ArrowUpRightIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { billingAPI } from '../services/api';
import { useTenantPath } from '../hooks/useTenantPath';
import { useAuthStore } from '../store/authStore';
import PhoneField from '../components/common/PhoneField';
import { displayLabel, formatDate } from '../utils/format';
import { phoneError } from '../utils/phone';
import { invalidateLicense } from '../hooks/useLicense';

// Billing & Payments — the tenant's own plan, prices and payment history.
//
// Deliberately a tier x period MATRIX rather than a single "upgrade" button.
// The upgrade-to-next-tier control this replaces could not express the two
// moves customers actually ask for: Growth-monthly -> Growth-annual, and
// annual -> monthly. Every plan/period combination the tenant is not currently
// on is payable, in both directions, at any tier.
//
// Prices and plan capacities come from GET /billing/self, which reads
// SubscriptionTiers.LIMITS server-side. Nothing about a plan is retyped here.

const money = (minor, currency) => {
  if (minor === null || minor === undefined) return null;
  const major = minor / 100;
  const locale = currency === 'INR' ? 'en-IN' : 'en-US';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency || 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(major);
  } catch (e) {
    return `${currency || ''} ${major.toLocaleString(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })}`.trim();
  }
};

// CURRENCY_FORMATTER_END — currency codes below this line are a pricing leak.

const num = (v) => (typeof v === 'number' ? v.toLocaleString() : null);

// The API owns the discount used to calculate the annual checkout amount. Do
// not derive it again from rounded prices in the browser.
const annualSavingPct = (price) => {
  const value = Number(price?.annual_discount_percent);
  return Number.isFinite(value) && value > 0 && value < 100 ? value : 0;
};

const formatPercent = (value) => Math.round(value).toString();

// Annual checkout is paid up front, but the comparable monthly cost is easier
// to scan across plans. Keep the full annual charge directly underneath so the
// customer cannot mistake the monthly equivalent for monthly billing.
const periodDisplayMinor = (minor, period) => (
  period === 'annual' && minor !== null && minor !== undefined ? minor / 12 : minor
);

// What a plan includes, from the catalogue the API sends. Typing these
// numbers into the bundle is how the pricing page and the platform drifted
// apart twice, so they are only ever rendered, never restated.
const planHighlights = (price) => [
  num(price.max_ai_answers) && `${num(price.max_ai_answers)} AI answers / month`,
  num(price.max_users) && `1 admin + ${num(price.max_users)} team member${price.max_users === 1 ? '' : 's'}`,
  num(price.max_agents) && `${num(price.max_agents)} AI agent${price.max_agents === 1 ? '' : 's'}`,
  num(price.max_knowledge_pages) && `${num(price.max_knowledge_pages)} knowledge pages`,
  num(price.included_notification_phones) && `${num(price.included_notification_phones)} WhatsApp alert number${price.included_notification_phones === 1 ? '' : 's'}`,
].filter(Boolean);


const PlanCard = ({
  price, period, name, highlights, saving, minor, currency,
  selected, locked, lockedNote, badge, onSelect, tax, withTax,
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    disabled={locked}
    onClick={() => onSelect(price.tier)}
    className={`rounded-xl border p-5 text-left flex flex-col h-full transition ${
      locked
        ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 cursor-not-allowed'
        : selected
          ? 'border-indigo-500 ring-2 ring-indigo-500 bg-white dark:bg-gray-800'
          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-indigo-300'
    }`}
  >
    <div className="flex items-start justify-between gap-2">
      <span className="text-base font-semibold text-gray-900 dark:text-white">{name}</span>
      {badge && (
        <span className="rounded-full bg-indigo-100 dark:bg-indigo-900/40 px-2 py-0.5 text-[11px] font-semibold text-indigo-700 dark:text-indigo-300 whitespace-nowrap">
          {badge}
        </span>
      )}
    </div>
    {price.description && (
      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{price.description}</p>
    )}
    <div className="mt-3">
      {minor !== null && minor !== undefined ? (
        <>
          <span className="text-2xl font-bold text-gray-900 dark:text-white">
            {money(periodDisplayMinor(minor, period), currency)}
          </span>
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {' / month'}
          </span>
          {period === 'annual' && (
            <div className="mt-1 text-xs font-semibold text-gray-700 dark:text-gray-200">
              Billed {money(minor, currency)} annually
            </div>
          )}
          {/* Every card states the amount actually charged. Showing the
              ex-tax price alone on the cards the tenant did not select would
              understate three of the four numbers on the page. */}
          {tax?.applies && (
            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              + {tax.rate_percent}% {tax.name} &middot; {period === 'annual' ? 'annual total' : 'you pay'}{' '}
              <span className="font-semibold text-gray-700 dark:text-gray-200">
                {money(withTax(minor), currency)}
              </span>
            </div>
          )}
          {period === 'annual' && price.offer_label && (
            <div className="mt-1 text-xs font-semibold text-indigo-700 dark:text-indigo-300">
              {price.offer_label}
            </div>
          )}
          {saving > 0 && (
            <div className="mt-1 text-xs font-semibold text-green-700 dark:text-green-400">
              Save {formatPercent(saving)}% versus paying monthly
            </div>
          )}
        </>
      ) : (
        <span className="text-sm text-gray-500 dark:text-gray-400">
          {period === 'annual' ? 'No annual price published' : 'No monthly price published'}
        </span>
      )}
    </div>
    <ul className="mt-4 space-y-1.5 text-xs text-gray-600 dark:text-gray-300">
      {highlights.map((h) => (
        <li key={h} className="flex gap-2">
          <CheckCircleIcon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-indigo-500" />
          <span>{h}</span>
        </li>
      ))}
    </ul>
    {lockedNote && (
      <p className="mt-4 text-xs font-medium text-gray-500 dark:text-gray-400">{lockedNote}</p>
    )}
  </button>
);


// Add-ons are the same whichever plan is chosen, so they are listed once and
// the chosen plan reads from the one basket.
// planNeeded(item): the plan an add-on needs when the chosen plan is too
// small for it (its min_tier), else null.
const AddOnPicker = ({ addons, basket, period, currency, disabled, onQuantity, planNeeded = () => null }) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
    <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-900 dark:text-white">
      Optional add-ons
    </h2>
    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
      Select what you need. The price below follows your Monthly or Annual choice.
    </p>
    <div className="mt-4 grid gap-3 sm:grid-cols-2">
      {addons.map((item) => {
        const unit = period === 'monthly' ? item.monthly_amount_minor : item.annual_amount_minor;
        const quantity = Number(basket?.[item.key] || 0);
        const isCapability = item.max_quantity === 1;
        const alreadyActive = isCapability && item.active_quantity > 0;
        const needsPlan = alreadyActive ? null : planNeeded(item);
        const unavailable = unit == null || disabled || alreadyActive || Boolean(needsPlan);
        return (
          <div
            key={item.key}
            className={`rounded-lg border p-3 ${
              quantity > 0
                ? 'border-indigo-400 bg-indigo-50/40 dark:bg-indigo-900/10'
                : 'border-gray-200 dark:border-gray-700'
            }`}
          >
            <div className="flex items-start gap-2.5">
              <input
                type="checkbox"
                checked={quantity > 0 || alreadyActive}
                disabled={unavailable}
                onChange={(event) => onQuantity(item.key, event.target.checked ? 1 : 0)}
                aria-label={`Add ${item.label}`}
                className="mt-0.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline justify-between gap-x-2">
                  <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                    {item.label}
                  </span>
                  <span className="text-right text-xs font-medium text-gray-700 dark:text-gray-200">
                    {alreadyActive
                      ? 'Active'
                      : needsPlan
                        ? `Only on ${needsPlan} and above`
                        : unit == null
                        ? 'Price not set'
                        : (
                          <>
                            <span className="block whitespace-nowrap">
                              {money(periodDisplayMinor(unit, period), currency)} / month
                            </span>
                            {period === 'annual' && (
                              <>
                                <span className="mt-0.5 block whitespace-nowrap text-[11px] text-gray-500 dark:text-gray-400">
                                  Billed {money(unit, currency)} annually
                                </span>
                                {annualSavingPct(item) > 0 && (
                                  <span className="mt-0.5 block whitespace-nowrap text-[11px] font-semibold text-green-700 dark:text-green-400">
                                    Save {formatPercent(annualSavingPct(item))}%
                                  </span>
                                )}
                              </>
                            )}
                          </>
                        )}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                  {item.description}
                </p>
                {item.trial_until && !alreadyActive && (
                  <p className="mt-1 text-xs text-indigo-700 dark:text-indigo-300">
                    Trial access until {formatDate(item.trial_until)}. Select to keep it after the trial.
                  </p>
                )}
                {!isCapability && item.active_quantity > 0 && quantity === 0 && (
                  <p className="mt-1 text-[11px] font-medium text-green-700 dark:text-green-400">
                    {item.active_quantity} active now · select to add more
                  </p>
                )}
                {!isCapability && quantity > 0 && (
                  <label className="mt-2 flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                    Quantity
                    <input
                      type="number"
                      min="1"
                      max={item.max_quantity}
                      step="1"
                      value={quantity}
                      onChange={(event) => onQuantity(
                        item.key,
                        Math.max(1, Math.min(item.max_quantity, Number(event.target.value) || 1)),
                      )}
                      className="w-16 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-2 py-1"
                      aria-label={`${item.label} quantity`}
                    />
                    {item.active_quantity > 0 && <span>{item.active_quantity} active now</span>}
                  </label>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);


const SummaryRow = ({ label, value, muted, strong }) => (
  <div role="group" aria-label={label} className={`flex justify-between gap-4 ${strong ? 'font-semibold text-gray-900 dark:text-white' : muted ? 'text-gray-500 dark:text-gray-400' : 'text-gray-700 dark:text-gray-200'}`}>
    <span>{label}</span>
    <span className="whitespace-nowrap">{value}</span>
  </div>
);


// Shown in place of prices when the server cannot say which currency to bill
// in: no back-office currency and no usable business mobile. Workspaces made
// in the back office never went through signup, which asks for the number.
// This page used to send them to "Contact us" for a number the tenant admin
// is allowed to set, and said "Plans will appear" to accounts that can only
// buy add-ons.
const CurrencySetupCard = ({ planPaid, onSaved }) => {
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    // A stored number that failed validation is prefilled so it can be fixed.
    billingAPI.getOwnBusinessPhone()
      .then(({ data }) => { if (!cancelled && data?.business_phone) setPhone(data.business_phone); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const save = async (event) => {
    event.preventDefault();
    const problem = phoneError(phone);
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await billingAPI.updateOwnBusinessPhone(phone.trim());
      onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save the number. Please try again.');
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={save}
      aria-labelledby="currency-setup-title"
      className="rounded-xl border-2 border-amber-400 bg-amber-50 p-5 shadow-sm dark:border-amber-500 dark:bg-amber-900/20"
    >
      <h2 id="currency-setup-title" className="text-lg font-bold text-gray-900 dark:text-white">
        {planPaid ? 'Add your business mobile to see add-on prices' : 'Add your business mobile to see plan prices'}
      </h2>
      <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">
        Your billing currency comes from your business mobile: Indian (+91) numbers are billed in rupees,
        all others in US dollars. This workspace has no business mobile yet.
      </p>
      <label htmlFor="billing-business-phone" className="mt-4 block text-sm font-semibold text-gray-900 dark:text-white">
        Business mobile
      </label>
      <PhoneField
        id="billing-business-phone"
        value={phone}
        onChange={(next) => { setPhone(next); setError(null); }}
        disabled={saving}
        required
        className="mt-2 flex-col sm:flex-row"
        selectClassName="w-full shrink-0 rounded-lg border border-gray-300 bg-white px-2.5 py-2.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:w-36"
        inputClassName="min-w-0 w-full flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
      />
      {error && (
        <p role="alert" className="mt-2 text-sm font-medium text-red-700 dark:text-red-300">{error}</p>
      )}
      <button
        type="submit"
        disabled={saving}
        className="mt-4 inline-flex min-h-[44px] items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'Save and show prices'}
      </button>
    </form>
  );
};

// How often to check whether a just-paid checkout has been applied.
// Written as a product so no plan's answer cap appears as a literal here.
const PAYMENT_POLL_INTERVAL_MS = 3 * 1000;

const bringIntoView = (element, block, focusTarget = element) => {
  if (!element) return;
  // jsdom and some older browsers have no scrollIntoView.
  element.scrollIntoView?.({ behavior: 'smooth', block });
  focusTarget?.focus?.({ preventScroll: true });
};

const BillingPage = () => {
  const tenantPath = useTenantPath();
  const workspaceName = useAuthStore((state) => state.user?.tenant_name) || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Annual is the default view: it is the cheaper plan and the one the
  // pricing page leads with, so the app should not open on the dearer option.
  const [period, setPeriod] = useState('annual');
  const [payingKey, setPayingKey] = useState(null);
  const [payError, setPayError] = useState(null);
  const [detailsForm, setDetailsForm] = useState(null);
  const [savingDetails, setSavingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState(null);
  // The details form sits at the top of the page and the Pay button at the
  // bottom. A customer sent up from Pay lands on the form, and is brought back
  // down to Pay once the saved details have cleared the blocker.
  const detailsPanelRef = useRef(null);
  const payButtonRef = useRef(null);
  const focusDetailsRef = useRef(false);
  const returnToPayRef = useRef(false);

  const usagePath = tenantPath('/plan-usage');

  const load = () => {
    setLoading(true);
    setError(null);
    billingAPI
      .getOwnBilling()
      .then((r) => setData(r.data))
      .catch((e) =>
        setError(e?.response?.data?.detail || e.message || 'Could not load billing.')
      )
      .finally(() => setLoading(false));
  };

  // Set when the gateway redirects the payer back here. Navigation only:
  // it names the payment to wait for, and proves nothing on its own.
  const [awaitingRef, setAwaitingRef] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('serviti_checkout_reference')
        || params.get('razorpay_payment_link_id')
        || null;
    } catch {
      return null;
    }
  });
  const [confirmTimedOut, setConfirmTimedOut] = useState(false);
  // Bumped by "Check again". The polling effect depends on it, so a retry
  // actually restarts the interval -- clearing the timeout flag alone left the
  // banner saying "Confirming" with nothing polling behind it.
  const [pollAttempt, setPollAttempt] = useState(0);

  useEffect(() => {
    load();
  }, []);

  // Drop the gateway's parameters from the address bar immediately, so a
  // refresh or a shared link does not re-enter the confirming state.
  useEffect(() => {
    if (!awaitingRef) return;
    try {
      window.history.replaceState({}, '', window.location.pathname);
    } catch {
      /* history is unavailable in some embedded contexts; harmless */
    }
  }, [awaitingRef]);

  // Wait for the WEBHOOK, not for the redirect. Poll until the server reports
  // this payment applied, then stop. Capped so a webhook that never arrives
  // ends in an explicit message rather than a spinner forever.
  useEffect(() => {
    if (!awaitingRef) return undefined;
    const started = Date.now();
    let cancelled = false;
    const timer = setInterval(async () => {
      if (cancelled) return;
      if (Date.now() - started > 90000) {
        clearInterval(timer);
        setConfirmTimedOut(true);
        return;
      }
      try {
        const { data } = await billingAPI.getOwnBilling();
        if (cancelled) return;
        setData(data);
        const paid = (data?.payments || []).some(
          (p) => (
            p.provider_ref === awaitingRef || p.checkout_reference === awaitingRef
          ) && p.applied,
        );
        if (paid) {
          clearInterval(timer);
          setAwaitingRef(null);
          setConfirmTimedOut(false);
          // What the tenant may use just changed: unlock it without a reload.
          invalidateLicense();
        }
      } catch {
        /* keep polling; a transient error is not a failed payment */
      }
    }, PAYMENT_POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [awaitingRef, pollAttempt]);

  // Open on the period the tenant already pays on, so they land on their own
  // plan rather than on a column they have to hunt for.
  const paidPeriod = data?.current?.period;
  useEffect(() => {
    if (paidPeriod) setPeriod(paidPeriod);
  }, [paidPeriod]);

  // Self-service changes start after the paid term ends. We do not invent
  // credits or replace an entitlement mid-term. A customer may renew the same
  // plan early; the server extends it from their existing expiry.
  // The live licence is the authority that the plan is already paid. A manual
  // superadmin payment has no captured gateway Payment row, so its period is
  // legitimately unknown; requiring paidPeriod here made the UI charge that
  // tenant for the plan again when they only selected add-ons.
  const activeTerm = Boolean(data?.current?.term_active);

  // Razorpay is reached through a hosted payment link, not an in-page modal:
  // the webhook signed over the raw body is the trust anchor, so the browser is
  // never in the path that decides whether a tenant is paid.
  // No currency is sent. The server resolves it from a verified business-phone
  // anchor or a superadmin-set account anchor; letting the browser name one
  // would let a customer quoted in USD ask to be charged the INR price instead.
  // Add-ons can be bought with a plan or, during an existing paid term, on
  // their own. One basket, shared by every plan: the order summary and the
  // pay button read the same object, so what is shown is what is sent.
  const [addons, setAddons] = useState(null);
  const [addonBasket, setAddonBasket] = useState({});
  // Which plan the summary and the button act on. Null until the tenant
  // picks; it then resolves to their own tier so the page opens on the plan
  // they already hold rather than on an arbitrary column.
  const [selectedTier, setSelectedTier] = useState(null);
  const trialChannelsSelected = useRef(false);

  // Add-ons are priced in the billing currency, so they are fetched again
  // when the customer sets the number that decides it.
  const [addonsVersion, setAddonsVersion] = useState(0);
  useEffect(() => {
    let cancelled = false;
    billingAPI.listOwnAddons()
      .then(({ data }) => { if (!cancelled) setAddons(data); })
      .catch(() => { if (!cancelled) setAddons(null); });
    return () => { cancelled = true; };
  }, [addonsVersion]);

  useEffect(() => {
    if (trialChannelsSelected.current || !addons || !data) return;
    const connected = data.current?.connected_trial_channels || [];
    if (!connected.length) return;
    trialChannelsSelected.current = true;
    const available = new Set((addons.addons || [])
      .filter((item) => !item.purchased && Number(item.active_quantity || 0) <= 0)
      .map((item) => item.key));
    setAddonBasket((basket) => ({
      ...Object.fromEntries(connected.filter((key) => available.has(key)).map((key) => [key, 1])),
      ...basket,
    }));
  }, [addons, data]);

  useEffect(() => {
    if (detailsForm === null || !focusDetailsRef.current) return;
    focusDetailsRef.current = false;
    const panel = detailsPanelRef.current;
    bringIntoView(panel, 'start', panel?.querySelector('input, select, textarea'));
  }, [detailsForm]);

  useEffect(() => {
    if (!returnToPayRef.current || !data || data.billing_details_required) return;
    returnToPayRef.current = false;
    bringIntoView(payButtonRef.current, 'center');
  }, [data]);

  const setAddonQuantity = (addon, quantity) => {
    setAddonBasket((all) => {
      const next = { ...all };
      if (quantity > 0) next[addon] = quantity;
      else delete next[addon];
      return next;
    });
  };

  const startCheckout = async (tier, chosenPeriod, addonBasket = {}, addonsOnly = false) => {
    const key = `${tier}:${chosenPeriod}`;
    if (payingKey) return;
    setPayingKey(key);
    setPayError(null);
    try {
      const res = await billingAPI.selfCheckout({
        tier: addonsOnly ? null : tier,
        period: chosenPeriod,
        addons: addonBasket,
      });
      const url = res?.data?.payment_link;
      if (!url) throw new Error('No payment link returned');
      window.location.assign(url);
    } catch (e) {
      setPayError(
        e?.response?.data?.detail || e.message || 'Could not start checkout. Please try again.'
      );
      setPayingKey(null);
    }
  };

  const saveDetails = async () => {
    if (savingDetails) return;
    setSavingDetails(true);
    setDetailsError(null);
    try {
      await billingAPI.saveOwnBillingDetails(detailsForm);
      setDetailsForm(null);
      load();
    } catch (e) {
      setDetailsError(
        e?.response?.data?.detail || e.message || 'Could not save billing details.'
      );
    } finally {
      setSavingDetails(false);
    }
  };

  // The server owns the commercial tier hierarchy. Price is only a fallback
  // during a mixed-version deploy: an offer can make a richer tier temporarily
  // cheaper, but it must never reorder the plan ladder or reverse the meaning
  // of Upgrade/Downgrade.
  const prices = useMemo(() => {
    const monthlyEquivalent = (p) =>
      p.monthly_amount_minor ??
      (p.annual_amount_minor !== null && p.annual_amount_minor !== undefined
        ? p.annual_amount_minor / 12
        : 0);
    return [...(data?.prices || [])].sort((a, b) => {
      if (Number.isInteger(a.rank) && Number.isInteger(b.rank)) return a.rank - b.rank;
      return monthlyEquivalent(a) - monthlyEquivalent(b);
    });
  }, [data]);

  if (loading && !data) {
    return <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading billing…</div>;
  }
  if (error && !data) {
    return (
      <div className="p-4 sm:p-6 max-w-5xl mx-auto">
        <div className="rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 p-4 text-sm text-red-800 dark:text-red-200 flex items-center justify-between gap-3">
          <span>{error}</span>
          <button type="button" onClick={load} className="text-xs font-medium underline">
            Retry
          </button>
        </div>
      </div>
    );
  }
  if (!data) return null;

  const current = data.current || {};
  const currentTier = String(current.tier || '').toLowerCase();
  const currentPeriod = current.period || null;
  const termEndsAt = current.term_ends_at ? formatDate(current.term_ends_at) : null;
  const providerActive = Boolean(data.provider_active);
  const payments = data.payments || [];

  // Direction comes from the server-owned tier hierarchy, never from price.
  // Keep the index fallback only for a mixed-version deploy where the API has
  // not started returning ranks yet.
  const currentIndex = prices.findIndex((p) => String(p.tier).toLowerCase() === currentTier);
  const currentRank = Number.isInteger(current.rank)
    ? current.rank
    : currentIndex === -1 ? null : currentIndex;

  // "Why am I being charged in dollars?" is a question a tenant admin will
  // actually ask. Name the durable account anchor rather than leaving the
  // currency unexplained.
  // ---- what is about to be bought -----------------------------------
  // Every number below and the button itself come from these, so the summary
  // cannot state one basket while the request sends another.
  const addonCatalogue = addons?.addons || [];
  const ownPrice = prices.find((p) => String(p.tier).toLowerCase() === currentTier);
  // During an active paid term the plan is not part of this basket. Keep the
  // selected tier pinned to what the tenant already owns; only add-ons can be
  // purchased until the term expires.
  const activeTier = activeTerm
    ? currentTier
    : selectedTier ?? String((ownPrice || prices[0] || {}).tier || '');
  const selectedPrice = prices.find((p) => String(p.tier) === String(activeTier)) || null;
  const planName = selectedPrice?.label || activeTier;
  const planMinor = selectedPrice
    ? (period === 'monthly' ? selectedPrice.monthly_amount_minor : selectedPrice.annual_amount_minor)
    : null;
  const planCurrency = selectedPrice?.currency || prices[0]?.currency;

  // Some add-ons are sold only on bigger plans (min_tier). One ticked before
  // switching to a smaller plan drops out of the summary AND the request.
  const rankOf = (tier) => {
    const index = prices.findIndex((p) => String(p.tier) === String(tier));
    return Number.isInteger(prices[index]?.rank) ? prices[index].rank : index;
  };
  const planNeeded = (item) => {
    if (!item.min_tier || rankOf(activeTier) >= rankOf(item.min_tier)) return null;
    const needed = prices.find((p) => String(p.tier) === String(item.min_tier));
    return needed?.label || item.min_tier;
  };
  const basketLines = addonCatalogue
    .map((item) => ({ ...item, quantity: Number(addonBasket[item.key] || 0) }))
    .filter((item) => item.quantity > 0 && !planNeeded(item));
  const chosenAddons = Object.fromEntries(basketLines.map((item) => [item.key, item.quantity]));
  const unitOf = (item) => (
    period === 'monthly' ? item.monthly_amount_minor : item.annual_amount_minor
  );
  const addonSubtotal = basketLines.reduce(
    (total, item) => total + (Number(unitOf(item)) || 0) * item.quantity, 0,
  );
  const addonMissingPrice = basketLines.some((item) => unitOf(item) == null);

  const isCurrentTier = currentTier === String(activeTier).toLowerCase();
  // "Current plan" needs BOTH to match: a Growth-monthly customer looking at
  // the annual column is making a change, not renewing.
  const changeLocked = activeTerm && !isCurrentTier;
  // During a paid term the plan stays untouched. Its captured period may be
  // unknown (manual mark-paid), but that cannot put the plan fee back into an
  // add-on basket: period here describes the add-on term only.
  const addonsOnly = activeTerm && isCurrentTier && addonSubtotal > 0;
  const checkoutSubtotal = (addonsOnly ? 0 : (Number(planMinor) || 0)) + addonSubtotal;
  const showOrderSummary = Boolean(selectedPrice) && (!activeTerm || addonsOnly);

  const selectedIndex = prices.findIndex((p) => String(p.tier) === String(activeTier));
  const selectedRank = Number.isInteger(selectedPrice?.rank)
    ? selectedPrice.rank
    : selectedIndex === -1 ? null : selectedIndex;
  const isUpgrade = currentRank !== null && selectedRank !== null && selectedRank > currentRank;
  const isDowngrade = currentRank !== null && selectedRank !== null && selectedRank < currentRank;
  // The summary above already names the plan, the period and the amount, so
  // the button is just the verb. This also retires "Switch to monthly", which
  // a tenant who had never been billed was shown as though they were changing
  // something. A move DOWN a tier is not called an upgrade: that would be the
  // one word on the page that is false.
  const payLabel = changeLocked
    ? `Plan changes open ${termEndsAt}`
    : payingKey
      ? 'Opening checkout…'
    : addonsOnly
      ? 'Pay for add-ons'
    : isUpgrade
      ? 'Upgrade and pay'
    : isDowngrade
      ? 'Downgrade and pay'
      : 'Pay';

  // The server decides this. It refuses an INR checkout without a place of
  // supply, so the page must not offer a Pay button it knows will 409. Keep
  // this declaration before payBlockedReason, which reads it during render.
  const detailsRequired = data.billing_details_required || null;
  // Without a currency nothing has a price, so there is nothing to choose.
  const currencyMissing = data.currency_source === 'missing';
  const tax = data.tax || {};
  // With nothing to add up, the total is simply what is due today.
  const itemisedTotal = addonsOnly || basketLines.length > 0 || Boolean(tax.applies);
  const gstStates = data.gst_states || [];
  const details = data.billing_details || {};
  const editingDetails = detailsForm !== null;
  // The server owns this invoicing rule. Do not derive it from currency in the
  // browser: the checkout request intentionally cannot choose a currency.
  const requiresBillingState = Boolean(details.requires_state);

  // A disabled button with no reason is the most common dead end on this
  // page, so name the blocker rather than greying out silently. Missing
  // billing details is the one a customer can fix, so it gets an action next
  // to the button: the form is at the top of a long page, out of sight.
  const detailsBlockPayment = !changeLocked && providerActive && Boolean(detailsRequired);
  const payBlockedReason = changeLocked
    ? `Plan and billing-period changes become available when your term ends on ${termEndsAt}.`
    : !providerActive
      ? 'Online payment is not switched on for this account yet.'
    : detailsBlockPayment
      ? detailsRequired
    : addonMissingPrice
      ? 'One of the selected add-ons has no price in your billing currency.'
    : planMinor == null && !addonsOnly
      ? `${planName} is not published for online purchase in your currency.`
      : null;

  const currencyReason = {
    business_phone: 'from your business mobile',
    explicit: 'set for your account',
    missing: 'not set',
  }[data.currency_source];

  const openDetails = () =>
    setDetailsForm({
      // The workspace name, never current.label: that is the plan name, and
      // an admin who saved the form as offered was invoiced as "Scale".
      legal_name: details.legal_name || workspaceName,
      address: details.address || '',
      state: details.state || '',
      country: details.country || '',
      gstin: details.gstin || '',
    });

  const openDetailsForPayment = () => {
    returnToPayRef.current = true;
    if (editingDetails) {
      const panel = detailsPanelRef.current;
      bringIntoView(panel, 'start', panel?.querySelector('input, select, textarea'));
      return;
    }
    focusDetailsRef.current = true;
    openDetails();
  };

  const cancelDetails = () => {
    returnToPayRef.current = false;
    setDetailsForm(null);
    setDetailsError(null);
  };

  // Shown on every card, because a price that is not what gets charged is
  // worse than no price. money() already formats in the resolved currency.
  const withTax = (minor) =>
    tax.applies && minor != null
      ? Math.round(minor * (1 + (tax.rate_percent || 0) / 100))
      : minor;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <CreditCardIcon className="w-6 h-6 text-indigo-500" />
            Billing &amp; Payments
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Your plan, how you&rsquo;re billed, and every payment on the account.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          title="Reload billing"
          className="p-1.5 text-gray-500 hover:text-gray-800 dark:text-gray-400 dark:hover:text-white"
        >
          <ArrowPathIcon className="w-4 h-4" />
        </button>
      </div>

      {awaitingRef && !confirmTimedOut && (
        <div className="rounded-lg border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 px-4 py-3 text-sm text-indigo-900 dark:text-indigo-200 flex items-center gap-3">
          <ArrowPathIcon className="w-4 h-4 animate-spin shrink-0" />
          <span>
            Confirming your payment with the gateway. Your plan updates here as
            soon as it is confirmed &mdash; this usually takes a few seconds.
          </span>
        </div>
      )}

      {confirmTimedOut && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
          <p className="font-semibold">Your payment has not been confirmed yet.</p>
          <p className="mt-1">
            If you were charged, nothing is lost &mdash; confirmation can take a
            little longer. Check again in a few minutes, and contact support
            with your payment reference if the plan has not changed.
          </p>
          <button
            type="button"
            onClick={() => { setConfirmTimedOut(false); setPollAttempt((n) => n + 1); }}
            className="mt-2 text-xs font-semibold underline"
          >
            Check again
          </button>
        </div>
      )}

      {/* Current plan */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Plan</div>
            <div className="text-lg font-bold capitalize text-gray-900 dark:text-white">
              {current.label || current.tier || 'Plan'}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Plan billed</div>
            <div className="text-lg font-semibold capitalize text-gray-900 dark:text-white">
              {currentPeriod || (activeTerm ? 'Set up by Serviti' : 'Not yet')}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Status</div>
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {displayLabel(current.status)}
            </div>
          </div>
          {current.subscription_started_at && (
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Since</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white">
                {formatDate(current.subscription_started_at)}
              </div>
            </div>
          )}
          {termEndsAt && (
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">Current term ends</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white">{termEndsAt}</div>
            </div>
          )}
          <Link
            to={usagePath}
            className="ml-auto text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline whitespace-nowrap"
          >
            View usage against limits →
          </Link>
        </div>
      </div>

      {current.status === 'past_due' && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
          Your trial has ended. Your dashboard is still available to renew, but your AI agent is paused until payment is confirmed.
        </div>
      )}

      {/* Invoicing identity. Asked for here rather than at signup: only a
          paying customer needs it, and the server refuses a taxable checkout
          without a place of supply, so it cannot be skipped either. */}
      <div
        ref={detailsPanelRef}
        className={`scroll-mt-24 rounded-xl border-2 p-5 ${
          !editingDetails && detailsRequired && providerActive
            ? 'border-amber-400 bg-amber-50 shadow-sm dark:border-amber-500 dark:bg-amber-900/20'
            : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
        }`}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                Billing details
              </h2>
              {!editingDetails && detailsRequired && providerActive && (
                <span className="rounded-full bg-amber-200 px-2.5 py-1 text-xs font-bold text-amber-950 dark:bg-amber-700 dark:text-white">
                  Required before payment
                </span>
              )}
            </div>
            <p className="mt-1 text-sm font-medium text-gray-700 dark:text-gray-200">
              Add the billing name and address that should appear on your invoice.
            </p>
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">
              {requiresBillingState
                ? 'Select the state where this business is registered.'
                : 'You can review or update these details before paying.'}
            </p>
          </div>
          {!editingDetails && (
            <button
              type="button"
              onClick={openDetails}
              className={details.legal_name
                ? 'rounded-lg border border-indigo-300 bg-white px-4 py-2 text-sm font-bold text-indigo-700 hover:bg-indigo-50 dark:border-indigo-600 dark:bg-gray-800 dark:text-indigo-300 dark:hover:bg-gray-700 whitespace-nowrap'
                : 'rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 whitespace-nowrap'}
            >
              {details.legal_name ? 'Edit billing details' : 'Add billing details'}
            </button>
          )}
        </div>

        {!editingDetails && details.legal_name && (
          <div className="mt-3 text-sm text-gray-700 dark:text-gray-200">
            <div className="font-semibold">{details.legal_name}</div>
            <div className="whitespace-pre-line text-gray-600 dark:text-gray-300">{details.address}</div>
            {details.state && (
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Place of supply: {(gstStates.find((g) => g.code === details.state) || {}).name || details.state}
              </div>
            )}
            {details.gstin && (
              <div className="text-xs text-gray-500 dark:text-gray-400">GSTIN: {details.gstin}</div>
            )}
          </div>
        )}

        {/* Only nag for details when there is actually something to pay for.
            With the provider switched off the page was telling the customer to
            add a billing address "before paying", directly above a banner
            saying they cannot pay at all -- two contradictory instructions for
            a purchase that is not possible yet. The panel stays open so anyone
            who wants to fill it in early still can. */}
        {!editingDetails && detailsRequired && providerActive && (
          <p className="mt-4 rounded-lg border border-amber-300 bg-white px-3 py-2.5 text-sm font-semibold text-amber-950 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
            Action required: {detailsRequired}
          </p>
        )}

        {editingDetails && (
          <div className="mt-4 space-y-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Registered name</span>
              <input
                type="text"
                value={detailsForm.legal_name}
                onChange={(e) => setDetailsForm({ ...detailsForm, legal_name: e.target.value })}
                placeholder="The legal entity the invoice is made out to"
                className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Billing address</span>
              <textarea
                rows={3}
                value={detailsForm.address}
                onChange={(e) => setDetailsForm({ ...detailsForm, address: e.target.value })}
                className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
              />
            </label>
            {requiresBillingState ? (
              <>
                <label className="block">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                    State where the business is registered
                  </span>
                  <select
                    value={detailsForm.state}
                    onChange={(e) => setDetailsForm({ ...detailsForm, state: e.target.value })}
                    className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                  >
                    <option value="">Select a state</option>
                    {gstStates.map((g) => (
                      <option key={g.code} value={g.code}>{g.name}</option>
                    ))}
                  </select>
                  <span className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                    Select the state this business is registered in.
                  </span>
                </label>
                {tax.applies && (
                  <label className="block">
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      GSTIN <span className="font-normal text-gray-400">(optional — needed to claim input credit)</span>
                    </span>
                    <input
                      type="text"
                      value={detailsForm.gstin}
                      maxLength={15}
                      onChange={(e) => setDetailsForm({ ...detailsForm, gstin: e.target.value.toUpperCase() })}
                      placeholder="29ABCDE1234F1Z5"
                      className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm font-mono text-gray-900 dark:text-white"
                    />
                  </label>
                )}
              </>
            ) : (
              <label className="block">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Country</span>
                <input
                  type="text"
                  value={detailsForm.country}
                  maxLength={2}
                  onChange={(e) => setDetailsForm({ ...detailsForm, country: e.target.value.toUpperCase() })}
                  placeholder="US"
                  className="mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-white"
                />
              </label>
            )}
            {detailsError && (
              <p className="rounded-lg bg-red-50 dark:bg-red-900/30 px-3 py-2 text-xs text-red-800 dark:text-red-200">
                {detailsError}
              </p>
            )}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={saveDetails}
                disabled={savingDetails}
                className="rounded-lg bg-indigo-600 px-3 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {savingDetails ? 'Saving…' : 'Save details'}
              </button>
              <button
                type="button"
                onClick={cancelDetails}
                className="rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm font-medium text-gray-700 dark:text-gray-200"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {!providerActive && (
        <div className="rounded-lg bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-4 py-3 text-sm text-amber-900 dark:text-amber-200">
          Online payment isn&rsquo;t switched on for this account yet.{' '}
          <a
            href="https://serviti.ai/contact"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold underline"
          >
            Contact us
          </a>{' '}
          and we&rsquo;ll send you a payment link for the plan you want.
        </div>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Payments are one-time for the period you choose. Serviti does not auto-debit your card; renew when your current term ends.
      </p>

      {activeTerm ? (
        <div className="rounded-xl border border-green-200 bg-green-50/60 p-4 dark:border-green-800 dark:bg-green-900/20">
          <p className="text-sm text-gray-700 dark:text-gray-200">
            Your current {currentPeriod && <><span className="font-semibold">{currentPeriod}</span>{' '}</>}
            plan is paid through <span className="font-semibold">{termEndsAt}</span>.
            Checkout below is for optional add-ons only; your plan will not be charged again.
            Need a different plan before then?{' '}
            <a
              href="https://serviti.ai/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-indigo-700 underline dark:text-indigo-300"
            >
              Ask us about changing early
            </a>.
          </p>
          {!currencyMissing && (
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <span className="text-sm font-semibold text-gray-900 dark:text-white">Add-on billing period</span>
              <div role="group" aria-label="Add-on billing period" className="inline-flex rounded-lg border border-green-300 bg-white p-1 dark:border-green-700 dark:bg-gray-800">
                {['monthly', 'annual'].map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => setPeriod(p)}
                    aria-pressed={period === p}
                    className={`min-w-24 rounded-md px-4 py-2 text-sm font-bold capitalize ${
                      period === p
                        ? 'bg-green-700 text-white shadow-sm'
                        : 'text-gray-700 hover:bg-green-50 dark:text-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : !currencyMissing && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4 dark:border-indigo-800 dark:bg-indigo-900/20">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white">
            Choose a plan and billing period
          </h2>
          <p className="mt-1 text-sm text-gray-700 dark:text-gray-200">
            Select Monthly or Annual first, then click the plan you want below.
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <span className="text-sm font-bold text-gray-900 dark:text-white">
              1. Select a billing period
            </span>
            <div
              role="group"
              aria-label="Billing period"
              className="inline-flex rounded-lg border-2 border-indigo-300 bg-white p-1 dark:border-indigo-700 dark:bg-gray-800"
            >
              {['monthly', 'annual'].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPeriod(p)}
                  aria-pressed={period === p}
                  className={`min-w-24 px-4 py-2 text-sm font-bold rounded-md capitalize transition-colors ${
                    period === p
                      ? 'bg-indigo-600 text-white shadow-sm'
                      : 'text-gray-700 dark:text-gray-200 hover:bg-indigo-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            {currentPeriod && currentPeriod !== period && (
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                You currently pay {currentPeriod}. Paying below switches you to {period}.
              </span>
            )}
          </div>
        </div>
      )}

      {currencyMissing ? (
        <CurrencySetupCard
          planPaid={activeTerm}
          onSaved={() => { load(); setAddonsVersion((v) => v + 1); }}
        />
      ) : prices.length === 0 ? (
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5 text-sm text-gray-500 dark:text-gray-400">
          No plans are published for online purchase yet.
        </div>
      ) : (
        <div>
          {!activeTerm && (
            <div className="mb-3">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">2. Select a plan</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                Click a plan card to select it. Your payment total updates below.
              </p>
            </div>
          )}
          <div
            role="radiogroup"
            aria-label="Plan"
            className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
          >
            {prices.map((price) => {
            const tierKey = String(price.tier);
            const own = currentTier === tierKey.toLowerCase();
            const cardMinor = period === 'monthly'
              ? price.monthly_amount_minor
              : price.annual_amount_minor;
            const cardLocked = activeTerm && !own;
            return (
              <PlanCard
                key={price.tier}
                price={price}
                period={period}
                name={price.label || tierKey}
                currency={price.currency}
                minor={cardMinor}
                saving={period === 'annual' ? annualSavingPct(price) : 0}
                highlights={planHighlights(price)}
                selected={String(activeTier) === tierKey}
                locked={cardLocked}
                lockedNote={cardLocked ? `Plan changes open on ${termEndsAt}, when your current term ends.` : null}
                badge={own ? 'Your plan' : null}
                onSelect={setSelectedTier}
                tax={tax}
                withTax={withTax}
              />
            );
            })}
          </div>
        </div>
      )}

      {selectedPrice && addonCatalogue.length > 0 && (
        <AddOnPicker
          addons={addonCatalogue}
          basket={addonBasket}
          period={period}
          currency={planCurrency}
          disabled={changeLocked}
          onQuantity={setAddonQuantity}
          planNeeded={planNeeded}
        />
      )}

      {showOrderSummary && (
        <div role="region" aria-labelledby="order-summary-title" className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-5">
          <h2 id="order-summary-title" className="text-sm font-semibold uppercase tracking-wide text-gray-900 dark:text-white">
            Order summary
          </h2>
          {!addonsOnly && (
            <div className="mt-4 rounded-lg bg-indigo-50 p-4 dark:bg-indigo-950/40">
              <p className="text-sm font-medium text-gray-700 dark:text-gray-200">
                {planName} · {period === 'annual' ? 'Annual plan' : 'Monthly plan'}
              </p>
              <p className="mt-2 text-3xl font-semibold tracking-tight text-gray-900 dark:text-white">
                {planMinor == null ? '—' : money(periodDisplayMinor(planMinor, period), planCurrency)}
                <span className="ml-1 text-sm font-normal tracking-normal text-gray-600 dark:text-gray-300">/ month</span>
              </p>
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {period === 'annual' ? 'Monthly equivalent' : 'Plan price'}
                {tax?.applies && ' · before tax'}
                {period === 'annual' && annualSavingPct(selectedPrice) > 0 && (
                  <span className="font-medium text-indigo-700 dark:text-indigo-300">
                    {' '}· Save {formatPercent(annualSavingPct(selectedPrice))}%
                  </span>
                )}
              </p>
            </div>
          )}
          <p className="mt-3 text-sm text-gray-600 dark:text-gray-300">
            {period === 'annual' ? 'Billed once for 12 months.' : 'Billed for one month.'} No automatic renewal.
          </p>
          <div className="mt-3 space-y-1.5 text-sm">
            {!addonsOnly && (isUpgrade || isDowngrade) && (
              <SummaryRow
                label={isDowngrade ? 'Downgrade' : 'Upgrade'}
                value={`${current.label || currentTier || 'Current plan'} → ${planName}`}
                strong
              />
            )}
            {addonsOnly ? (
              <SummaryRow
                label={`${planName} plan`}
                value="unchanged — your term continues"
                muted
              />
            ) : (basketLines.length > 0 || tax?.applies) && (
              <SummaryRow
                label={`${planName} plan · ${period === 'annual' ? '12 months, billed annually' : 'monthly'}`}
                value={planMinor == null ? '—' : money(planMinor, planCurrency)}
              />
            )}
            {basketLines.map((item) => (
              <SummaryRow
                key={item.key}
                label={`${item.quantity > 1 ? `${item.label} × ${item.quantity}` : item.label} · ${period === 'annual' ? '12 months, billed annually' : 'monthly'}`}
                value={unitOf(item) == null
                  ? 'price not set'
                  : money((Number(unitOf(item)) || 0) * item.quantity, planCurrency)}
              />
            ))}
            {(basketLines.length > 0 || tax?.applies) && (
              <div className="border-t border-gray-200 dark:border-gray-700 pt-1.5">
                <SummaryRow label="Subtotal" value={money(checkoutSubtotal, planCurrency)} />
              </div>
            )}
            {tax?.applies && (
              <SummaryRow
                label={`${tax.rate_percent}% ${tax.name}`}
                value={money(withTax(checkoutSubtotal) - checkoutSubtotal, planCurrency)}
                muted
              />
            )}
            {/* The amount charged, set large and directly above the button, so
                the monthly equivalent headlining the summary is never read as
                today's charge. */}
            <div
              role="group"
              aria-label={itemisedTotal ? 'Total due today' : 'Due today'}
              className="flex items-baseline justify-between gap-4 border-t border-gray-200 pt-3 text-gray-900 dark:border-gray-700 dark:text-white"
            >
              <span className="text-base font-semibold">{itemisedTotal ? 'Total due today' : 'Due today'}</span>
              <span className="whitespace-nowrap text-2xl font-bold tracking-tight">
                {money(withTax(checkoutSubtotal), planCurrency)}
              </span>
            </div>
          </div>
          {/* Above the button, not in small print below it: a greyed-out Pay
              button read as broken when the only explanation was a caption. */}
          {payBlockedReason && (
            <div
              className="mt-4 flex items-start gap-2.5 rounded-lg border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-100"
            >
              <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-300" aria-hidden="true" />
              <div className="min-w-0 flex-1">
                <p id="pay-blocked-reason" className="font-semibold">{payBlockedReason}</p>
                {detailsBlockPayment && (
                  <button
                    type="button"
                    onClick={openDetailsForPayment}
                    className="mt-2 inline-flex min-h-[44px] items-center rounded-lg bg-indigo-600 px-4 py-2 text-sm font-bold text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    {details.legal_name ? 'Complete billing details' : 'Add billing details'}
                  </button>
                )}
              </div>
            </div>
          )}
          <button
            ref={payButtonRef}
            type="button"
            disabled={Boolean(payBlockedReason) || Boolean(payingKey)}
            aria-describedby={payBlockedReason ? 'pay-blocked-reason' : undefined}
            onClick={() => startCheckout(activeTier, period, chosenAddons, addonsOnly)}
            className="mt-4 inline-flex w-full items-center justify-center gap-1 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
          >
            {payLabel === 'Pay' ? (period === 'annual' ? 'Pay for 12 months' : 'Pay for 1 month') : payLabel}
            {!payBlockedReason && <ArrowUpRightIcon className="w-4 h-4" />}
          </button>
          {period === 'annual' && !activeTerm && (
            <p className="mt-4 text-sm text-gray-600 dark:text-gray-300">
              Prefer a smaller upfront payment?{' '}
              <button
                type="button"
                onClick={() => setPeriod('monthly')}
                disabled={Boolean(payingKey)}
                className="inline-flex min-h-[44px] items-center font-semibold text-indigo-700 underline underline-offset-2 hover:text-indigo-900 disabled:opacity-50 dark:text-indigo-300 dark:hover:text-indigo-200"
              >
                Switch to monthly
              </button>
            </p>
          )}
        </div>
      )}

      {payError && (
        <div className="rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 px-4 py-3 text-sm text-red-800 dark:text-red-200">
          {payError}
        </div>
      )}

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Payment opens with our payment provider on a secure hosted page. Serviti never sees or stores
        your card details. Your plan updates once the payment is confirmed.
      </p>

      {/* Payment history */}
      <div>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-900 dark:text-white mb-2">
          Payment history
          {payments.length >= 20 && (
            <span className="ml-2 normal-case font-normal text-xs text-gray-500 dark:text-gray-400">
              (most recent 20)
            </span>
          )}
        </h2>
        {payments.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No payments on this account yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700/50 text-left text-gray-600 dark:text-gray-300">
                <tr>
                  <th className="px-3 py-2 font-semibold">Date</th>
                  <th className="px-3 py-2 font-semibold">Plan</th>
                  <th className="px-3 py-2 font-semibold">Period</th>
                  <th className="px-3 py-2 font-semibold">Amount</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 dark:text-gray-200">
                {payments.map((p, i) => (
                  <tr key={`${p.created_at}-${i}`} className="border-t border-gray-100 dark:border-gray-700">
                    <td className="px-3 py-2 whitespace-nowrap">
                      {formatDate(p.created_at)}
                    </td>
                    <td className="px-3 py-2 capitalize">
                      {p.tier || (p.addons?.length ? 'Add-ons only' : '—')}
                      {p.addons?.length > 0 && (
                        <div className="mt-0.5 normal-case text-[11px] text-gray-500 dark:text-gray-400">
                          {p.addons.map((item) => `${displayLabel(item.key)} × ${item.quantity}`).join(', ')}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 capitalize">{p.period || '—'}</td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {money(p.amount_minor, p.currency) || '—'}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded text-xs font-semibold ${
                          p.status === 'captured'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200'
                            : p.status === 'failed'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200'
                        }`}
                      >
                        {displayLabel(p.status)}
                      </span>
                      {['created', 'creating'].includes(p.status) && (
                        <div className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                          If you paid, confirmation can take a few minutes. Please don&rsquo;t pay again; if
                          this has not changed within an hour,{' '}
                          <a href="mailto:sales@serviti.ai" className="underline">email us</a>.
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BillingPage;
